import axios from 'axios'

import { ACCESS_TOKEN_KEY } from '@/constants/app'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'
const USES_COOKIE_AUTH = import.meta.env.VITE_AUTH_MODE === 'cookie'

export class ApiError extends Error {
  constructor({ message, status, code, fieldErrors, details, isNetworkError = false }) {
    super(message)
    this.name = 'ApiError'
    this.status = status ?? null
    this.code = code ?? null
    /** Map of `fieldName -> message`, ready to feed into React Hook Form. */
    this.fieldErrors = fieldErrors ?? null
    /**
     * A record the server sent along with the refusal, for the cases where the
     * answer to an error is another record — the walk-in duplicate check returns
     * the open lead it found under `existing`, so the caller can open it instead
     * of creating a second one.
     */
    this.details = details ?? null
    this.isNetworkError = isNetworkError
  }

  get isUnauthorized() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  get isNotFound() {
    return this.status === 404
  }

  get isValidationError() {
    return this.status === 422 || this.status === 400
  }
}

const STATUS_MESSAGES = {
  400: 'The request could not be processed. Check the highlighted fields.',
  401: 'Your session has expired. Sign in again to continue.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested record could not be found.',
  409: 'This record was changed elsewhere. Reload and try again.',
  422: 'Some fields need attention before this can be saved.',
  429: 'Too many requests. Wait a moment and try again.',
  500: 'Something went wrong on our end. Try again shortly.',
}

/**
 * Accepts the common server error shapes and produces one predictable object so
 * feature code never branches on transport details.
 */
const normalizeError = (error) => {
  if (axios.isCancel(error)) {
    return new ApiError({ message: 'Request cancelled', code: 'CANCELLED' })
  }

  if (!error.response) {
    return new ApiError({
      message: 'Cannot reach the server. Check your connection and try again.',
      code: 'NETWORK',
      isNetworkError: true,
    })
  }

  const { status, data } = error.response
  // This API returns { success, msg, error } where `msg` is generic
  // ("Invalid request") and `error` carries the specific cause
  // ("Invalid credentials"), so the specific one wins. `message` covers the more
  // common convention.
  const message =
    data?.error ?? data?.msg ?? data?.message ?? STATUS_MESSAGES[status] ?? 'Unexpected error.'

  return new ApiError({
    message,
    status,
    code: data?.code,
    fieldErrors: data?.errors ?? data?.fieldErrors ?? null,
    details: data?.existing ?? data?.details ?? null,
  })
}

/**
 * The default timeout is sized for ordinary reads and writes, which answer in
 * under a second. A few endpoints walk an external API form by form — a Meta
 * sync crosses forty Graph calls and takes twenty seconds with nothing to do —
 * and those pass this instead. Aborting one of those is worse than waiting: the
 * work is already happening server-side, so the browser only loses the report of
 * what it did.
 */
export const LONG_RUNNING_TIMEOUT_MS = 180_000

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 20000,
  withCredentials: USES_COOKIE_AUTH,
  headers: { Accept: 'application/json' },
})

/**
 * Registered by <Auth> so a rejected token can end the session without this
 * module importing the store (which would import the API back).
 */
let onUnauthorized = null
export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler
}

apiClient.interceptors.request.use((config) => {
  // The default Authorization header is set by services/jwt.service.js. This
  // covers the first request after a reload, before setSession has run.
  if (!USES_COOKIE_AUTH && !config.headers.Authorization) {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY)
    if (token) config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const normalized = normalizeError(error)
    // Ending the session lets the route guard redirect; no imperative
    // navigation from inside the transport layer.
    if (normalized.isUnauthorized) onUnauthorized?.()
    return Promise.reject(normalized)
  },
)

/**
 * Unwraps the envelopes this API uses — `{ result: ... }` and `{ data: ... }` —
 * while tolerating a bare payload.
 */
export const unwrap = (response) => {
  const body = response.data
  if (!body || typeof body !== 'object') return body
  if ('result' in body) return body.result
  if ('data' in body) return body.data
  return body
}

/**
 * Reads a paginated list response. Supports both `meta.total` and a flat
 * `total`, so a change of API convention is a one-line edit here.
 */
export const unwrapList = (response) => {
  const body = response.data ?? {}
  const items = body.result ?? body.data ?? body.items ?? []
  const meta = body.meta ?? body
  return {
    items,
    total: meta.total ?? items.length,
    page: meta.page ?? 1,
    pageSize: meta.pageSize ?? meta.per_page ?? items.length,
  }
}
